import { useState } from 'react';

import {
  Autocomplete,
  Chip,
  FormControlLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography
} from '@mui/material';

import { sanitizeFtsText, sanitizeSocialHandle } from '@/constants/regex';
import { inputSx } from '@/constants/sx';
import { useFetchWorkspaceLogos } from '@/hooks/queries/workspaces';
import { Evaluation } from '@/models/evaluations';

interface Step2Props {
  values: Evaluation.Form;
  onChange: (field: keyof Evaluation.Form, value: unknown) => void;
  isDisabled?: boolean;
  showSubtitle?: boolean;
}

type Mode = 'any' | 'specific';

const SPONSORSHIP_OPTIONS = [
  { value: -1, label: 'All' },
  { value: 1, label: 'Sponsored' },
  { value: 0, label: 'Not sponsored' }
] as const;

const TRISTATE_OPTIONS = [
  { value: 'any', label: 'All' },
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' }
] as const;

const WHICH_OPTIONS = [
  { value: 'any', label: 'Any' },
  { value: 'specific', label: 'Specific' }
] as const;

const useFtsTextInput = () => {
  const [inputValue, setInputValue] = useState('');
  const [isComposing, setIsComposing] = useState(false);

  const clean = (value: string) => sanitizeFtsText(value).replace(/\s+/g, '');

  return {
    inputValue,
    onInputChange: (_: unknown, value: string) =>
      setInputValue(isComposing ? value : clean(value)),
    inputProps: {
      onCompositionStart: () => setIsComposing(true),
      onCompositionEnd: (e: React.CompositionEvent<HTMLInputElement>) => {
        setIsComposing(false);
        setInputValue(clean((e.target as HTMLInputElement).value));
      },
      onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === ' ' && !isComposing) {
          e.preventDefault();
        }
      }
    }
  };
};

const useSocialHandleInput = () => {
  const [inputValue, setInputValue] = useState('');

  const clean = (value: string) => sanitizeSocialHandle(value);

  return {
    inputValue,
    onInputChange: (_: unknown, value: string) => {
      setInputValue(clean(value));
    }
  };
};

const Step2 = ({
  values,
  onChange,
  isDisabled = false,
  showSubtitle = true
}: Step2Props) => {
  const { platformDisclosure, urls, logos, tags, hashtags } = values;

  const tagsField = useSocialHandleInput();
  const hashtagField = useFtsTextInput();

  const [urlMode, setUrlMode] = useState<Mode>(
    urls?.patterns?.length ? 'specific' : 'any'
  );
  const [logoMode, setLogoMode] = useState<Mode>(
    logos?.hasLogo === 'true' && logos.brands?.length ? 'specific' : 'any'
  );

  const { data: workspaceLogos, isLoading: isLogosLoading } =
    useFetchWorkspaceLogos({ params: { page: '1', size: '100' } });

  const handleRadioChange = (
    field: 'platformDisclosure' | 'urls' | 'logos',
    value: string
  ) => {
    if (field === 'platformDisclosure') {
      onChange(field, Number(value) as -1 | 0 | 1);
      return;
    }

    const hasValue = value === 'true';

    if (field === 'urls') {
      if (!hasValue) setUrlMode('any');
      onChange('urls', {
        hasLinks: value,
        patterns: hasValue ? urls?.patterns || [] : []
      });
      return;
    }

    if (!hasValue) setLogoMode('any');
    onChange(
      'logos',
      hasValue
        ? {
            hasLogo: 'true',
            brands: (logos?.hasLogo === 'true' ? logos.brands : []) ?? []
          }
        : { hasLogo: value as 'any' | 'false' }
    );
  };

  const handleUrlModeChange = (value: Mode) => {
    setUrlMode(value);
    if (value === 'any') {
      onChange('urls', { hasLinks: urls.hasLinks, patterns: [] });
    }
  };

  const handleLogoModeChange = (value: Mode) => {
    setLogoMode(value);
    if (value === 'any') {
      onChange('logos', { hasLogo: logos.hasLogo, brands: [] });
    }
  };

  return (
    <>
      <Stack mb={3}>
        {showSubtitle && (
          <Typography variant="h6" fontWeight={300} color="textSecondary">
            Specify some extra details to analyse to assure the best results.
          </Typography>
        )}
      </Stack>
      <Stack direction="row" gap={6}>
        <Stack gap={2} flex={1}>
          {/* SPONSORSHIP */}
          <Stack
            direction="row"
            alignItems="center"
            gap={2}
            id="evaluation-sponsorship"
          >
            <Typography color="textSecondary" width={130} textAlign="right">
              Sponsorship:
            </Typography>
            <RadioGroup
              row
              value={String(platformDisclosure)}
              onChange={(e) =>
                handleRadioChange('platformDisclosure', e.target.value)
              }
            >
              {SPONSORSHIP_OPTIONS.map(({ value, label }) => (
                <FormControlLabel
                  key={value}
                  value={String(value)}
                  disabled={isDisabled}
                  control={<Radio size="small" />}
                  label={label}
                />
              ))}
            </RadioGroup>
          </Stack>

          <Stack gap={0.25} id="evaluation-urls">
            <Stack
              direction="row"
              alignItems="center"
              gap={2}
              id="evaluation-urls"
            >
              <Typography color="textSecondary" width={130} textAlign="right">
                URLs:
              </Typography>
              <RadioGroup
                row
                value={urls?.hasLinks || 'any'}
                onChange={(e) => handleRadioChange('urls', e.target.value)}
              >
                {TRISTATE_OPTIONS.map(({ value, label }) => (
                  <FormControlLabel
                    key={value}
                    value={value}
                    disabled={isDisabled}
                    control={<Radio size="small" />}
                    label={label}
                  />
                ))}
              </RadioGroup>
            </Stack>

            {/* URLS - Which: Any/Specific (only show if Yes selected) */}
            {urls?.hasLinks === 'true' && (
              <Stack
                direction="row"
                alignItems="flex-start"
                gap={2}
                id="evaluation-urls-which"
              >
                <Typography
                  color="textSecondary"
                  width={130}
                  textAlign="right"
                  mt={1}
                >
                  Which:
                </Typography>
                <Stack gap={1} flex={1}>
                  <RadioGroup
                    row
                    value={urlMode}
                    onChange={(e) =>
                      handleUrlModeChange(e.target.value as Mode)
                    }
                  >
                    {WHICH_OPTIONS.map(({ value, label }) => (
                      <FormControlLabel
                        key={value}
                        value={value}
                        disabled={isDisabled}
                        control={<Radio size="small" />}
                        label={label}
                      />
                    ))}
                  </RadioGroup>

                  {/* Show input only if Specific is selected */}
                  {urlMode === 'specific' && (
                    <Autocomplete
                      multiple
                      freeSolo
                      fullWidth
                      size="small"
                      disabled={isDisabled}
                      options={[]}
                      value={urls.patterns ?? []}
                      onChange={(_, value) =>
                        onChange('urls', {
                          hasLinks: urls.hasLinks,
                          patterns: value
                        })
                      }
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                          const { key, ...tagProps } = getTagProps({ index });
                          return (
                            <Chip
                              key={key}
                              size="small"
                              label={option}
                              {...tagProps}
                            />
                          );
                        })
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Type or paste terms"
                          inputProps={{
                            ...params.inputProps,
                            onKeyDown: (
                              e: React.KeyboardEvent<HTMLInputElement>
                            ) => {
                              if (e.key === ' ') {
                                e.preventDefault();
                              }
                            }
                          }}
                        />
                      )}
                    />
                  )}
                </Stack>
              </Stack>
            )}
          </Stack>

          {/* LOGOS - Main tristate */}
          <Stack gap={0.25} id="evaluation-logo">
            <Stack
              direction="row"
              alignItems="center"
              gap={2}
              id="evaluation-logo-detection"
            >
              <Typography color="textSecondary" width={130} textAlign="right">
                Logo:
              </Typography>
              <RadioGroup
                row
                value={logos?.hasLogo || 'any'}
                onChange={(e) => handleRadioChange('logos', e.target.value)}
              >
                {TRISTATE_OPTIONS.map(({ value, label }) => (
                  <FormControlLabel
                    key={value}
                    value={value}
                    disabled={isDisabled}
                    control={<Radio size="small" />}
                    label={label}
                  />
                ))}
              </RadioGroup>
            </Stack>

            {/* LOGOS - Which: Any/Specific (only show if Yes selected) */}
            {logos?.hasLogo === 'true' && (
              <Stack
                direction="row"
                alignItems="flex-start"
                gap={2}
                id="evaluation-logo-which"
              >
                <Typography
                  color="textSecondary"
                  width={130}
                  textAlign="right"
                  mt={1}
                >
                  Which:
                </Typography>
                <Stack gap={1} flex={1}>
                  <RadioGroup
                    row
                    value={logoMode}
                    onChange={(e) =>
                      handleLogoModeChange(e.target.value as Mode)
                    }
                  >
                    {WHICH_OPTIONS.map(({ value, label }) => (
                      <FormControlLabel
                        key={value}
                        value={value}
                        disabled={isDisabled}
                        control={<Radio size="small" />}
                        label={label}
                      />
                    ))}
                  </RadioGroup>

                  {logoMode === 'specific' && (
                    <Autocomplete
                      multiple
                      freeSolo
                      fullWidth
                      size="small"
                      disabled={isDisabled || isLogosLoading}
                      options={workspaceLogos?.results ?? []}
                      value={logos?.brands ?? []}
                      onChange={(_, value) =>
                        onChange('logos', {
                          hasLogo: logos.hasLogo,
                          brands: value
                        })
                      }
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                          const { key, ...tagProps } = getTagProps({ index });
                          return (
                            <Chip
                              key={key}
                              size="small"
                              label={option}
                              {...tagProps}
                            />
                          );
                        })
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Select logos" />
                      )}
                    />
                  )}
                </Stack>
              </Stack>
            )}
          </Stack>
        </Stack>

        {/* RIGHT COLUMN */}
        <Stack gap={2} flex={1} sx={inputSx}>
          <Autocomplete
            id="evaluation-tags"
            multiple
            freeSolo
            fullWidth
            autoSelect
            size="small"
            disabled={isDisabled}
            options={[]}
            value={tags ?? []}
            inputValue={tagsField.inputValue}
            onInputChange={tagsField.onInputChange}
            onChange={(_, value) =>
              onChange(
                'tags',
                value.filter((v) => v.length >= 2)
              )
            }
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip key={key} size="small" label={option} {...tagProps} />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tags"
                inputProps={params.inputProps}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">@</InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  )
                }}
              />
            )}
          />
          <Autocomplete
            id="evaluation-hashtags"
            multiple
            freeSolo
            fullWidth
            autoSelect
            size="small"
            disabled={isDisabled}
            options={[]}
            value={hashtags ?? []}
            inputValue={hashtagField.inputValue}
            onInputChange={hashtagField.onInputChange}
            onChange={(_, value) =>
              onChange(
                'hashtags',
                value.map((v) =>
                  typeof v === 'string' ? sanitizeFtsText(v) : v
                )
              )
            }
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip key={key} size="small" label={option} {...tagProps} />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Hashtags"
                inputProps={{
                  ...params.inputProps,
                  ...hashtagField.inputProps
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">#</InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  )
                }}
              />
            )}
          />
        </Stack>
      </Stack>
    </>
  );
};

export default Step2;
